import React, { useState, useEffect, ChangeEvent } from 'react';
import { Panel, Label, Button } from '@uhg-harmony/uitk-react';
import { Arrow } from 'builder/scenes/SiteBuilder/SettingsPanel/SettingsPanel';
import {
  SizeLocationData,
  WidgetSizeLocation,
} from '../../../builder/components/WidgetSizeLocation';
import { Config } from './types';
import {
  SizeLocationPanel,
  StyledPanel,
  StyledPanelTitle,
} from '../Text/TextWidgetControl';
import ButtonPanel from 'builder/components/WidgetConfigurations/Button/ButtonPanel';
import {
  MainLabelStyles,
  Typography,
} from 'builder/components/WidgetConfigurations/Typography/Typography';
import {
  Background,
  BackgroundData,
  ColorButtonWrapper,
} from 'builder/components/WidgetConfigurations/Background/Background';
import { Border } from 'builder/components/WidgetConfigurations/Border';
import styled from 'styled-components';
import {
  StyledCheckBox,
  StyledForm,
  StyledFormControl,
  StyledInnerDiv,
  StyledOuterDiv,
  StyledSelect,
  TextWrapperDiv,
  WrapperDiv,
} from '../Link/LinkWidgetControl';
import GradientColorPicker, {
  StyledPickerPopover,
  StyledPickerWrapper,
  StyledPickerHeader,
  StyledClose,
} from 'builder/components/GradientColorPicker/GradientColorPicker';
import S3UploadFile from 'builder/components/S3UploadFile';
import {
  PDF_AND_WORD_REGEX,
  VALID_UPLOAD_FILES,
  defaultLinkOptions,
  buttonWidgetTrackingContent,
} from 'builder/util/constants';
import { UploadedFile } from '../shared/types';
import { ColorButton } from 'builder/components/ColorButton/ColorButton';
import {
  OptionalLabel,
  StyledTextInput,
} from 'builder/components/WidgetConfigurations/TextInput/TextInputPanel';
import { IconButton } from '@material-ui/core';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from 'shared/components/Tooltip';

const TypographyPanel = styled(Panel)``;

export const ButtonContainer = styled.div`
  display: flex;
  background: #ffffff;
  padding: 1rem;
  display: ruby-text;
  width: 100%;
`;

export const RegularButton = styled(Button)<{ currentTab: number }>`
  font-family: OptumSans, sans-serif;
  border: solid 1px #64636f;
  border-radius: 5px 0 0 5px;
  border-color: #64636f;
  background: #ffffff;
  color: #64636f;
  height: 2rem;
  width: 45%;
  display: block;
  padding: 0rem;
  font-weight: normal;
  :hover {
    ${p =>
      p.currentTab === 0
        ? 'box-shadow: none; background: #64636f; font-weight: bold; color: #ffffff;'
        : 'box-shadow: none; background: #ffffff; font-weight: normal; color: #64636f; border: solid 1px #64636f;'}
  }
  :focus {
    box-shadow: none;
    background: #64636f;
    font-weight: bold;
    color: #ffffff;
  }
  ${p =>
    p.currentTab === 0
      ? 'box-shadow: none; background: #64636f; font-weight: bold; color: #ffffff;'
      : ''}
`;

export const HoverButton = styled(Button)<{ currentTab: number }>`
  font-family: OptumSans, sans-serif;
  border: solid 1px #64636f;
  border-radius: 0 5px 5px 0;
  border-color: #64636f;
  background: #ffffff;
  color: #64636f;
  height: 2rem;
  width: 45%;
  display: block;
  padding: 0rem;
  font-weight: normal;
  :hover {
    ${p =>
      p.currentTab === 1
        ? 'box-shadow: none; background: #64636f; font-weight: bold; color: #ffffff;'
        : 'box-shadow: none; background: #ffffff; font-weight: normal; color: #64636f; border: solid 1px #64636f;'}
  }
  :focus {
    box-shadow: none;
    background: #64636f;
    font-weight: bold;
    color: #ffffff;
  }
  ${p =>
    p.currentTab === 1
      ? 'box-shadow: none; background: #64636f; font-weight: bold; color: #ffffff;'
      : ''}
`;

export const StyledPanelGroup = styled(Panel.Group)`
  margin-top: -1.5rem;
`;

export const LinkOptionsWrapper = styled.div`
  padding-top: 1.5rem;
  display: inline-flex;
  flex-direction: column;
  & > select {
    line-height: 1.25rem;
  }
`;

export const ButtonWidgetControl: React.FC<WidgetControlProps<Config>> = (
  props: WidgetControlProps<Config>
) => {
  const {
    value,
    onChange,
    widgetState,
    pages,
    currentPage: { layoutLocked },
  } = props;
  const { displayText, buttonAnalyticsText } = widgetState.config;
  const [currentTab, setCurrentTab] = useState(widgetState.config.currentTab);
  const [linkType, setLinkType] = useState(widgetState.config.linkType);
  const [pageValue, setPageValue] = useState(
    widgetState.config.selectedPage?.id.toString()
  );
  const [pickerAnchorEl, setPickerAnchorEl] = useState<HTMLDivElement | null>(
    null
  );
  const pickerOpen = Boolean(pickerAnchorEl);
  const pickerId = pickerOpen ? 'simple-popover' : undefined;

  useEffect(() => {
    if (linkType === 'None') {
      onChange({
        ...value,
        linkType: 'None',
        url: '',
        newTab: false,
        iconColor: '',
        selectedPage: null,
      });
      setPageValue('');
    }
  }, [linkType]);

  useEffect(() => {
    setLinkType(value.linkType);
    setCurrentTab(value.currentTab);
    setPageValue(value.selectedPage?.id.toString());
  }, [widgetState.config]);

  const handleCurrentTab = e => {
    switch (e.target.id) {
      case 'regular':
        setCurrentTab(0);
        onChange({
          ...value,
          currentTab: 0,
        });
        break;
      case 'hover':
        setCurrentTab(1);
        onChange({
          ...value,
          currentTab: 1,
        });
        break;
      default:
    }
  };

  const updateWidgetSizeLocation = (sizeLoc: SizeLocationData) => {
    onChange({
      ...value,
      width: sizeLoc.width,
      height: sizeLoc.height,
      xLocation: sizeLoc.xLocation,
      yLocation: sizeLoc.yLocation,
      uniqueId: sizeLoc.uniqueId,
    });
  };

  const handleLineColor = data => {
    switch (currentTab) {
      case 0:
        onChange({
          ...value,
          lineColor: data,
        });
        break;
      case 1:
        onChange({
          ...value,
          hoverData: {
            ...value.hoverData,
            lineColor: data,
          },
        });
        break;
      default:
    }
  };

  const handleIconStyle = data => {
    switch (currentTab) {
      case 0:
        onChange({
          ...value,
          iconStyle: data,
        });
        break;
      case 1:
        onChange({
          ...value,
          hoverData: {
            ...value.hoverData,
            iconStyle: data,
          },
        });
        break;
      default:
    }
  };

  const handleIcon = (data: Image) => {
    switch (currentTab) {
      case 0:
        onChange({
          ...value,
          icon: data,
        });
        break;
      case 1:
        onChange({
          ...value,
          hoverData: {
            ...value.hoverData,
            icon: data,
          },
        });
        break;
      default:
    }
  };

  const handleIconPlacement = data => {
    switch (currentTab) {
      case 0:
        onChange({
          ...value,
          iconPlacement: data,
        });
        break;
      case 1:
        onChange({
          ...value,
          hoverData: {
            ...value.hoverData,
            iconPlacement: data,
          },
        });
        break;
      default:
    }
  };

  const handleIconAltText = data => {
    switch (currentTab) {
      case 0:
        onChange({
          ...value,
          iconAltText: data,
        });
        break;
      case 1:
        onChange({
          ...value,
          hoverData: {
            ...value.hoverData,
            iconAltText: data,
          },
        });
        break;
      default:
    }
  };

  const handleBoldData = data => {
    switch (currentTab) {
      case 0:
        onChange({
          ...value,
          bold: data,
        });
        break;
      case 1:
        onChange({
          ...value,
          hoverData: {
            ...value.hoverData,
            bold: data,
          },
        });
        break;
      default:
    }
  };

  const handleItalic = data => {
    switch (currentTab) {
      case 0:
        onChange({
          ...value,
          italic: data,
        });
        break;
      case 1:
        onChange({
          ...value,
          hoverData: {
            ...value.hoverData,
            italic: data,
          },
        });
        break;
      default:
    }
  };

  const handleAlignment = data => {
    switch (currentTab) {
      case 0:
        onChange({
          ...value,
          align: data,
        });
        break;
      case 1:
        onChange({
          ...value,
          hoverData: {
            ...value.hoverData,
            align: data,
          },
        });
        break;
      default:
    }
  };

  const handleTextColorData = (data: string) => {
    switch (currentTab) {
      case 0:
        onChange({
          ...value,
          textColor: data,
        });
        break;
      case 1:
        onChange({
          ...value,
          hoverData: {
            ...value.hoverData,
            textColor: data,
          },
        });
        break;
      default:
    }
  };

  const handleTextHighlightColorData = data => {
    onChange({
      ...value,
      textHighlightColor: data,
    });
  };

  const handleFontSize = data => {
    switch (currentTab) {
      case 0:
        onChange({
          ...value,
          fontSize: data,
        });
        break;
      case 1:
        onChange({
          ...value,
          hoverData: {
            ...value.hoverData,
            fontSize: data,
          },
        });
        break;
      default:
    }
  };

  const handleFont = data => {
    switch (currentTab) {
      case 0:
        onChange({
          ...value,
          font: data,
        });
        break;
      case 1:
        onChange({
          ...value,
          hoverData: {
            ...value.hoverData,
            font: data,
          },
        });
        break;
      default:
    }
  };

  const handleLineSpacing = data => {
    {
      switch (currentTab) {
        case 0:
          onChange({
            ...value,
            lineSpacing: data,
          });
          break;
        case 1:
          onChange({
            ...value,
            hoverData: {
              ...value.hoverData,
              lineSpacing: data,
            },
          });
          break;
        default:
      }
    }
  };

  const handleCharacterSpacing = data => {
    {
      switch (currentTab) {
        case 0:
          onChange({
            ...value,
            characterSpacing: data,
          });
          break;
        case 1:
          onChange({
            ...value,
            hoverData: {
              ...value.hoverData,
              characterSpacing: data,
            },
          });
          break;
        default:
      }
    }
  };

  const handleUnderline = data => {
    {
      switch (currentTab) {
        case 0:
          onChange({
            ...value,
            underline: data,
          });
          break;
        case 1:
          onChange({
            ...value,
            hoverData: {
              ...value.hoverData,
              underline: data,
            },
          });
          break;
        default:
      }
    }
  };

  const handleStrikeThrough = data => {
    {
      switch (currentTab) {
        case 0:
          onChange({
            ...value,
            strikeThrough: data,
          });
          break;
        case 1:
          onChange({
            ...value,
            hoverData: {
              ...value.hoverData,
              strikeThrough: data,
            },
          });
          break;
        default:
      }
    }
  };

  const updateBorderData = (data: BorderData) => {
    switch (currentTab) {
      case 0:
        onChange({
          ...value,
          borderData: data,
        });
        break;
      case 1:
        onChange({
          ...value,
          hoverData: {
            ...value.hoverData,
            borderData: data,
          },
        });
        break;
      default:
    }
  };

  const onDisplayTextChange = (e: ChangeEvent<any>) => {
    e.preventDefault();
    const { config } = props.widgetState;
    onChange({
      ...config,
      displayText: e.target.value,
    });
  };

  const onAnalyticsTextChange = (e: ChangeEvent<any>) => {
    e.preventDefault();
    const { config } = props.widgetState;
    onChange({
      ...config,
      buttonAnalyticsText: e.target.value,
    });
  };

  const handleEmailAddress = (e: ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...value,
      emailAddress: e.target.value,
    });
  };

  const handleEmailSubject = (e: ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...value,
      emailSubject: e.target.value,
    });
  };

  const setValue = (e: ChangeEvent<HTMLInputElement>) => {
    setLinkType(e.target.value);
    onChange({
      ...value,
      url: '',
      selectedPage: null,
      linkType: e.target.value,
    });
  };

  const getMinimalPageReference = (page: Page) => ({
    id: page.id,
    current: { slug: page.current.slug, title: page.current.title },
  });

  const handlePage = (e: ChangeEvent<HTMLSelectElement>) => {
    e.preventDefault();
    const valueInt = parseInt(e.target.value);
    if (isNaN(valueInt)) {
      setPageValue('');
      console.warn('Invalid page id selected:', e.target.value);
      return;
    }
    const found = pages.find(page => page.id === valueInt);
    if (found) {
      setPageValue(e.target.value);
      const safeSlug = encodeURIComponent(found.current.slug);
      onChange({
        ...value,
        // Store only the minimal reference — avoids embedding the full page
        // object (with its entire widget content) inside this page's content
        // JSON, which was causing OOM and inflated DB payload sizes.
        selectedPage: (getMinimalPageReference(found) as unknown) as Page,
        url: `https://${props.site.domain}/${safeSlug}`,
      });
    } else {
      setPageValue('');
      console.warn('Page not found for selected value:', e.target.value);
    }
  };

  const handleUrl = (e: ChangeEvent<HTMLInputElement>) => {
    if (linkType === 'URL') {
      onChange({
        ...value,
        url: e.target.value,
      });
    }
  };

  const handleUpload = (file: UploadedFile) => {
    onChange({
      ...value,
      url: file.url,
      uploadedFile: file,
    });
  };

  const handleNewTabCheck = (e: ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...value,
      newTab: e.target.checked,
    });
  };

  const handleColorEditClick = (e: React.MouseEvent<any>) => {
    e.stopPropagation();
    e.preventDefault();
    setPickerAnchorEl(e.currentTarget);
  };

  const handlePickerClick = (e: React.MouseEvent<HTMLInputElement>) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const handlePickerClose = (e: React.MouseEvent<any>) => {
    e.stopPropagation();
    setPickerAnchorEl(null);
  };

  const handleIconColor = data => {
    onChange({
      ...value,
      iconColor: data,
    });
  };

  const handleColorChange = (newColor: string) => {
    handleIconColor(newColor);
  };

  const handleBackgroundColor = (newColor: string) => {
    switch (currentTab) {
      case undefined:
      case 0:
        onChange({
          ...value,
          backgroundData: {
            ...value.backgroundData,
            color: newColor,
          },
        });
        break;
      case 1:
        onChange({
          ...value,
          hoverData: {
            ...value.hoverData,
            backgroundData: {
              ...value.hoverData.backgroundData,
              color: newColor,
            },
          },
        });
        break;
      default:
    }
  };

  const updateBackgroundData = (data: BackgroundData) => {
    switch (currentTab) {
      case undefined:
      case 0:
        onChange({
          ...value,
          backgroundData: data,
        });
        break;
      case 1:
        onChange({
          ...value,
          hoverData: {
            ...value.hoverData,
            backgroundData: data,
          },
        });
        break;
      default:
    }
  };

  return (
    <>
      <ButtonContainer>
        <RegularButton
          id="regular"
          onPress={handleCurrentTab}
          currentTab={currentTab}
          data-test-id="regular-button"
        >
          Regular
        </RegularButton>
        <HoverButton
          id="hover"
          onPress={handleCurrentTab}
          currentTab={currentTab}
          data-test-id="hover-button"
        >
          Hover
        </HoverButton>
      </ButtonContainer>
      {currentTab === 0 ? (
        <StyledPanelGroup>
          <StyledPanel>
            <Panel
              title={<Label style={StyledPanelTitle}>General</Label>}
              icon={Arrow}
              data-test-id="regular-general-panel"
            >
              <ButtonPanel
                data-test-id="general-section-for-button-component-regular"
                disabled={layoutLocked}
                pages={props.pages}
                site={props.site}
                updateSite={props.updateSite}
                saveSite={props.saveSite}
                widgetState={widgetState}
                currentTab={currentTab}
                handleIcon={handleIcon}
                handleLineColor={handleLineColor}
                handleIconStyle={handleIconStyle}
                handleIconPlacement={handleIconPlacement}
                handleIconAltText={handleIconAltText}
              />
              <MainLabelStyles>Display text</MainLabelStyles>
              <StyledTextInput
                name="displayText"
                value={displayText}
                onChange={onDisplayTextChange}
                data-test-id="display-text-input"
              />
              <WrapperDiv>
                <TextWrapperDiv>
                  <MainLabelStyles style={{ marginBottom: '0.25rem' }}>
                    Button Name
                  </MainLabelStyles>
                  <OptionalLabel>(optional)</OptionalLabel>
                  <Tooltip
                    title={buttonWidgetTrackingContent}
                    arrow
                    placement="bottom-start"
                    style={{ marginLeft: '8px' }}
                  >
                    <IconButton>
                      <InfoOutlinedIcon
                        fontSize="small"
                        style={{ color: 'darkblue' }}
                      />
                    </IconButton>
                  </Tooltip>
                </TextWrapperDiv>
                <StyledTextInput
                  name="buttonAnalyticsText"
                  value={buttonAnalyticsText}
                  onChange={onAnalyticsTextChange}
                  data-test-id="display-analytics-text-input"
                />
              </WrapperDiv>
              <LinkOptionsWrapper>
                <MainLabelStyles data-test-id="link-actions-label">
                  What do you want to link to?
                </MainLabelStyles>
                <StyledSelect
                  onChange={e => setValue(e)}
                  value={linkType}
                  data-test-id="link-options-dropdown"
                  style={{ fontFamily: 'OptumSans' }}
                >
                  {defaultLinkOptions.map(option => {
                    return (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    );
                  })}
                </StyledSelect>
              </LinkOptionsWrapper>
              {linkType === 'Email' && (
                <>
                  <StyledTextInput
                    name="emailAddress"
                    value={widgetState.config.emailAddress}
                    onChange={handleEmailAddress}
                    placeholder="Type email address here"
                    data-test-id="email-address-input"
                  />

                  <StyledTextInput
                    name="emailSubject"
                    value={widgetState.config.emailSubject}
                    onChange={handleEmailSubject}
                    placeholder="Type email subject line here"
                    style={{ marginTop: '10px' }}
                    data-test-id="email-subject-input"
                  />
                </>
              )}
              {linkType === 'Page' && (
                <StyledFormControl id="controlled-select">
                  <StyledSelect
                    onChange={handlePage}
                    value={pageValue}
                    data-test-id="page-select-dropdown"
                    style={{ fontFamily: 'OptumSans' }}
                  >
                    <option value="none">Select a Page</option>
                    {pages.map(
                      page =>
                        page.current.slug !== 'defaultContentPage' && (
                          <option key={page.id} value={page.id}>
                            {page.current.title}
                          </option>
                        )
                    )}
                  </StyledSelect>
                </StyledFormControl>
              )}
              {linkType === 'URL' && (
                <StyledTextInput
                  name="webaddress"
                  value={widgetState.config.url}
                  onChange={handleUrl}
                  placeholder="http:// or https://"
                  data-test-id="web-address-input"
                />
              )}
              {linkType === 'File' && (
                <S3UploadFile
                  label="File (.pdf, .doc, .docx format only)"
                  onUpload={handleUpload}
                  file={widgetState.config.uploadedFile}
                  accept={VALID_UPLOAD_FILES}
                  icon="Upload"
                  iconProps={{
                    width: '1.2em',
                    height: '1.3em',
                    viewBox: '0 0 24 24',
                  }}
                  fileFormatRegex={PDF_AND_WORD_REGEX}
                  data-test-id={`file-uploader`}
                />
              )}
              {(linkType === 'Page' ||
                linkType === 'URL' ||
                linkType === 'File') && (
                <StyledOuterDiv>
                  <StyledForm id="webaddress-checkbox">
                    <StyledCheckBox
                      checked={widgetState.config.newTab}
                      onChange={handleNewTabCheck}
                    >
                      Open in new tab
                    </StyledCheckBox>
                  </StyledForm>
                  {widgetState.config.newTab && (
                    <StyledInnerDiv>
                      Icon color
                      <ColorButtonWrapper
                        id="icon-color"
                        onClick={handleColorEditClick}
                      >
                        <ColorButton color={widgetState.config.iconColor} />
                      </ColorButtonWrapper>
                    </StyledInnerDiv>
                  )}
                  <StyledPickerPopover
                    data-test-id="widget-color-picker-popover"
                    id={pickerId}
                    open={pickerOpen}
                    onClick={handlePickerClick}
                    onClose={handlePickerClose}
                    anchorEl={pickerAnchorEl}
                    anchorOrigin={{
                      vertical: 'center',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'center',
                      horizontal: 'right',
                    }}
                  >
                    <StyledPickerWrapper>
                      <StyledPickerHeader data-test-id="styled-picker-header">
                        Color Selector
                      </StyledPickerHeader>
                      <StyledClose
                        data-test-id="close-icon"
                        onClick={handlePickerClose}
                      />
                    </StyledPickerWrapper>
                    <GradientColorPicker
                      value={widgetState.config.iconColor}
                      onChange={handleColorChange}
                      site={props.site}
                      handleColorChange={handleColorChange}
                      updateSite={props.updateSite}
                      saveSite={props.saveSite}
                      anchorEl={pickerAnchorEl}
                    />
                  </StyledPickerPopover>
                </StyledOuterDiv>
              )}
            </Panel>
          </StyledPanel>
          <StyledPanel>
            <TypographyPanel
              title={<Label style={StyledPanelTitle}>Typography</Label>}
              initiallyClosed
              icon={Arrow}
              data-test-id="regular-typography-panel"
            >
              <Typography
                data-test-id="typography-component-regular"
                disabled={layoutLocked}
                boldData={handleBoldData}
                handleFontSize={handleFontSize}
                handleFont={handleFont}
                handleCharacterSpacing={handleCharacterSpacing}
                handleLineSpacing={handleLineSpacing}
                handleItalicData={handleItalic}
                handleUnderLine={handleUnderline}
                handleStrikeThrough={handleStrikeThrough}
                handleTextColor={handleTextColorData}
                handleTextHighlightColor={handleTextHighlightColorData}
                leftAlignData={handleAlignment}
                centerAlignData={handleAlignment}
                rightAlignData={handleAlignment}
                pages={props.pages}
                site={props.site}
                updateSite={props.updateSite}
                saveSite={props.saveSite}
                widgetState={widgetState}
                currentTab={currentTab}
              />
            </TypographyPanel>
          </StyledPanel>
          <StyledPanel>
            <SizeLocationPanel
              title={<Label style={StyledPanelTitle}>Size and Location</Label>}
              initiallyClosed
              icon={Arrow}
              data-test-id="button-size-location-panel"
            >
              <WidgetSizeLocation
                data-test-id="widget-size-location-regular"
                disabled={layoutLocked}
                sizeLocation={{
                  xLocation: value.xLocation,
                  yLocation: value.yLocation,
                  width: value.width,
                  height: value.height,
                  uniqueId: value.uniqueId,
                }}
                updateWidgetSizeLocation={updateWidgetSizeLocation}
                activeWidgetId={widgetState.id}
              />
            </SizeLocationPanel>
          </StyledPanel>
          <StyledPanel>
            <Panel
              key="regular-background-panel"
              title={<Label style={StyledPanelTitle}>Background</Label>}
              initiallyClosed
              icon={Arrow}
              data-test-id="regular-background-panel"
            >
              <Background
                key="regular-background-component"
                data-test-id="regular-background-component"
                disabled={layoutLocked}
                backgroundData={updateBackgroundData}
                widgetState={widgetState}
                handleLineColor={handleLineColor}
                handleBackgroundColor={handleBackgroundColor}
                site={props.site}
                updateSite={props.updateSite}
                saveSite={props.saveSite}
                currentTab={currentTab}
              />
            </Panel>
          </StyledPanel>
          <StyledPanel>
            <Panel
              title={<Label style={StyledPanelTitle}>Border</Label>}
              initiallyClosed
              icon={Arrow}
              data-test-id="regular-border-panel"
            >
              <Border
                data-test-id="border-data-regular"
                updateBorderData={updateBorderData}
                disabled={layoutLocked}
                handleLineColor={handleLineColor}
                widgetState={widgetState}
                pages={props.pages}
                site={props.site}
                updateSite={props.updateSite}
                saveSite={props.saveSite}
                currentTab={currentTab}
              />
            </Panel>
          </StyledPanel>
        </StyledPanelGroup>
      ) : (
        <StyledPanelGroup>
          <StyledPanel>
            <Panel
              title={<Label style={StyledPanelTitle}>General</Label>}
              initiallyClosed
              icon={Arrow}
              data-test-id="hover-general-panel"
            >
              <ButtonPanel
                data-test-id="general-section-for-button-component-hover"
                disabled={layoutLocked}
                pages={props.pages}
                site={props.site}
                updateSite={props.updateSite}
                saveSite={props.saveSite}
                widgetState={widgetState}
                currentTab={currentTab}
                handleIcon={handleIcon}
                handleLineColor={handleLineColor}
                handleIconStyle={handleIconStyle}
                handleIconPlacement={handleIconPlacement}
                handleIconAltText={handleIconAltText}
              />
            </Panel>
          </StyledPanel>
          <StyledPanel>
            <TypographyPanel
              title={<Label style={StyledPanelTitle}>Typography</Label>}
              initiallyClosed
              icon={Arrow}
              data-test-id="hover-typography-panel"
            >
              <Typography
                data-test-id="typography-component-hover"
                disabled={layoutLocked}
                boldData={handleBoldData}
                handleFontSize={handleFontSize}
                handleFont={handleFont}
                handleCharacterSpacing={handleCharacterSpacing}
                handleLineSpacing={handleLineSpacing}
                handleItalicData={handleItalic}
                handleUnderLine={handleUnderline}
                handleStrikeThrough={handleStrikeThrough}
                handleTextColor={handleTextColorData}
                handleTextHighlightColor={handleTextHighlightColorData}
                leftAlignData={handleAlignment}
                centerAlignData={handleAlignment}
                rightAlignData={handleAlignment}
                pages={props.pages}
                site={props.site}
                updateSite={props.updateSite}
                saveSite={props.saveSite}
                widgetState={widgetState}
                currentTab={currentTab}
              />
            </TypographyPanel>
          </StyledPanel>
          <StyledPanel>
            <Panel
              key="hover-background-panel"
              title={<Label style={StyledPanelTitle}>Background</Label>}
              initiallyClosed
              icon={Arrow}
              data-test-id="hover-background-panel"
            >
              <Background
                key="hover-background-component"
                disabled={layoutLocked}
                data-test-id="hover-background-component"
                backgroundData={updateBackgroundData}
                widgetState={widgetState}
                handleLineColor={handleLineColor}
                handleBackgroundColor={handleBackgroundColor}
                site={props.site}
                updateSite={props.updateSite}
                saveSite={props.saveSite}
                currentTab={currentTab}
              />
            </Panel>
          </StyledPanel>
          <StyledPanel>
            <Panel
              title={<Label style={StyledPanelTitle}>Border</Label>}
              initiallyClosed
              icon={Arrow}
              data-test-id="hover-border-panel"
            >
              <Border
                data-test-id="border-data-hover"
                updateBorderData={updateBorderData}
                disabled={layoutLocked}
                handleLineColor={handleLineColor}
                widgetState={widgetState}
                currentTab={currentTab}
                pages={props.pages}
                site={props.site}
                updateSite={props.updateSite}
                saveSite={props.saveSite}
              />
            </Panel>
          </StyledPanel>
        </StyledPanelGroup>
      )}
    </>
  );
};

export default ButtonWidgetControl;
